import { Injectable, Type, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ModalService } from './modal.service';
import { ModalConfig } from './modal-config';
import { ModalRef } from './modal-ref';

class DeferredModalRef<T = any, R = any> {
  public componentInstance: T | null = null;
  public disableClose: boolean = false;

  private readonly _afterClosed = new Subject<R | undefined>();
  private readonly _backdropClick = new Subject<MouseEvent>();
  private _boundRef: ModalRef<T, R> | null = null;
  private _pendingClose = false;
  private _pendingCloseResult: R | undefined;

  public bind(ref: ModalRef<T, R>): void {
    this._boundRef = ref;
    this.componentInstance = ref.componentInstance;
    this.disableClose = ref.disableClose;

    ref.backdropClick().subscribe((event) => this._backdropClick.next(event));
    ref.afterClosed().subscribe((result) => {
      this._afterClosed.next(result);
      this._afterClosed.complete();
      this._backdropClick.complete();
    });

    if (this._pendingClose) {
      ref.close(this._pendingCloseResult);
    }
  }

  public close(result?: R): void {
    if (this._boundRef) {
      this._boundRef.close(result);
      return;
    }

    this._pendingClose = true;
    this._pendingCloseResult = result;
  }

  public afterClosed(): Observable<R | undefined> {
    return this._afterClosed.asObservable();
  }

  public backdropClick(): Observable<MouseEvent> {
    return this._backdropClick.asObservable();
  }
}

@Injectable({
  providedIn: 'root'
})
export class StackedModalsService {

  private modalStack : { componentOrTemplateRef: Type<any>, config: ModalConfig<any> | undefined, deferredRef?: DeferredModalRef<any, any> }[] = [];
  private openedModal: boolean = false;
  public ObservableModalStackLength: BehaviorSubject<number>;

  public readonly modalService = inject(ModalService);
  private readonly _ngZone = inject(NgZone);

  constructor() {
    this.ObservableModalStackLength = new BehaviorSubject<number>(0);
  }

  public openModal<T, D = any, R = any>(componentOrTemplateRef: Type<T>, config?: ModalConfig<D>): ModalRef<T, R> {
    if (this.openedModal === false) {
      const modalRef = this.openModalWindow(componentOrTemplateRef, config);
      this.openedModal = true;
      return modalRef;
    }
    else {
      const deferredRef = new DeferredModalRef<T, R>();
      let modal = { componentOrTemplateRef: componentOrTemplateRef, config: config, deferredRef: deferredRef as DeferredModalRef<any, any> };
      this.modalStack.push(modal);
      return deferredRef as unknown as ModalRef<T, R>;
    }
  }

  public openModalNext<T, D = any, R = any>(componentOrTemplateRef: Type<T>, config?: ModalConfig<D>): ModalRef<T, R> {
    if (this.openedModal === false) {
      const modalRef = this.openModalWindow(componentOrTemplateRef, config);
      this.openedModal = true;
      return modalRef;
    }
    else {
      const deferredRef = new DeferredModalRef<T, R>();
      let modal = { componentOrTemplateRef: componentOrTemplateRef, config: config, deferredRef: deferredRef as DeferredModalRef<any, any> };
      this.modalStack.unshift(modal);
      return deferredRef as unknown as ModalRef<T, R>;
    }
  }

  public clearStack(): void {
    this.modalStack = [];
  }

  public openModalWindow<T, D = any, R = any>(componentOrTemplateRef: Type<T>, config?: ModalConfig<D>, deferredRef?: DeferredModalRef<T, R>): ModalRef<T, R> {
    let modalRef!: ModalRef<T, R>;

    this._ngZone.run(() => {
      modalRef = this.modalService.open(componentOrTemplateRef, config);

      if (deferredRef) {
        deferredRef.bind(modalRef);
      }

      modalRef.afterClosed().subscribe(() => {
        this.checkStack();
      });
    });

    return modalRef;
  }

  private checkStack(): void {
    this.ObservableModalStackLength.next(this.modalStack.length);
    if (this.modalStack.length === 0) {
      this.openedModal = false;
    }
    else {
      const nextModal = this.modalStack[0];
      this.openModalWindow(nextModal.componentOrTemplateRef, nextModal.config, nextModal.deferredRef);
      this.modalStack.shift();
    }
  }
}
